import { autoinject, transient } from "aurelia-dependency-injection";
import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { bindable } from "aurelia-templating";
import { ClientSideValidator, EntityValidationsService } from "../../../../../../../client-validation-system/validations-service";
import { ValidationActivation, ValidationTypeEnum } from "../../../../../../../client-validation-system/validations.models";
import { Messages } from "../../../../../../../configuration/event-messages.constants";
import { Claims } from "../../../../../../../constants/authentication/claims";
import { LookupValue } from "../../../../../../../models/lookups/lookup.models";
import { UserClaimsService } from "../../../../../../../services/authentication/user-claims.service";
import { UserClientService } from "../../../../../../../services/authentication/user-client.service";
import { NotificationClientService, NotificationType } from "../../../../../../../services/basics/notification-client.service";
import { AssignedVesselPortCallModel } from "../../../models/ports-and-terminals.models";
import { PortsAndTerminalsService } from "../../../services/ports-and-terminals.service";
import { computedFrom } from "aurelia-binding";
import { Reflection } from "../../../../../../../utilities/reflection";

@autoinject
@transient()
export class VesselPreArrivalAssignment {

    private _validator: ClientSideValidator;

    @bindable
    public vesselPortCallId: number;

    @bindable
    public canSetUnassignedAfterAssignment: boolean = true;

    @bindable
    public canReassign: boolean = true;

    @bindable
    public canAssignToMe: (() => boolean);

    public previousAssignment: number;
    public isAssigning: boolean = false;
    public assignedVesselPortCall: AssignedVesselPortCallModel;
    public isMenuOpen: boolean;
    public isReassignEditing: boolean;
    public canAssign: boolean = true;
    public assignmentComment: string;
    public isRsUser: boolean;

    public set assignedTo(value: number) {
        if (value) {
            this.assignedVesselPortCall.assignedTo = value;
        }
    }

    @computedFrom(Reflection.getPropertyPath<VesselPreArrivalAssignment>(z => z.assignedVesselPortCall),
    Reflection.getPropertyPath<VesselPreArrivalAssignment>(z => z.assignedVesselPortCall.assignedTo))
    public get assignedTo(): number {
        return this.assignedVesselPortCall.assignedTo;
    }

    constructor(private userService: UserClientService,
        private userClaimsService: UserClaimsService,
        private eventAggregator: EventAggregator,
        private portsAndTerminalsService: PortsAndTerminalsService, private entityValidationsService: EntityValidationsService,
        private notificationService: NotificationClientService) {
        this.isRsUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
    }

    stopPropagation($event) {
        $event.stopPropagation();
    }

    assignmentClick($event) {
        if ($event)
            $event.stopPropagation();
    }

    assignmentDropdownClick($event) {
        if ($event)
            $event.stopPropagation();
    }

    _subscriptionToDocumentClickEvent: Subscription;

    attached() {
        this._subscriptionToDocumentClickEvent = this.eventAggregator.subscribe(Messages.documentClick, this.closePopup);
    }

    detached() {
        if (this._subscriptionToDocumentClickEvent)
            this._subscriptionToDocumentClickEvent.dispose();
    }

    closePopup = (x) => {
        if (x && x.source == 'document') {
            this.isMenuOpen = false;
        }
    };

    async reload() {
        const promise = await this.portsAndTerminalsService.getVesselPortCallAssignment({
            vesselPortCallId: this.vesselPortCallId
        });
        const assignedVesselPortCall = promise.assignedVesselPortCall;
        this.assignedVesselPortCall = assignedVesselPortCall;
        this.previousAssignment = assignedVesselPortCall.assignedTo;
        if (!this.assignedVesselPortCall) {
            this.assignedVesselPortCall = {
                assignedTo: null
            };
        }

        const rules = {
            activation: ValidationActivation.Always,
            entityName: "Assignment",
            isCollection: false,
            nestedRulesHolder: [],
            rules: [{
                errorMessage: "Comments are required",
                parameters: null, property: "assignmentComment",
                validationType: ValidationTypeEnum.Required
            }]
        };
        this._validator = this.entityValidationsService.getValidator(this, rules);
        return this.assignedVesselPortCall;
    }

    vesselPortCallIdChanged() {
        if (this.vesselPortCallId) {
            this.reload();
        }
    }

    async save(userId: number) {
        if (this.isAssigning) return;

        this.isAssigning = true;
        try {
            await this.portsAndTerminalsService.setVesselPortCallAssignment({
                vesselPortCallId: this.vesselPortCallId,
                assignedTo: userId,
                assignmentComment: this.assignmentComment
            });
            this.assignedVesselPortCall.assignmentComment = '';
            this.assignedVesselPortCall.assignedTo = userId;
            this.previousAssignment = userId;
            this.assignmentComment = '';
            this.isMenuOpen = false;
            this.isAssigning = false;
            this.isReassignEditing = false;
            this.notificationService.show("Assignment Updated Successfully", "", NotificationType.Success);
            await this.reload();
        } catch (e) {
            this.isAssigning = false;
            this.notificationService.show("Assignment update failed", "", NotificationType.Error);
            throw e;
        }
    }

    cancel() {
        this.assignedVesselPortCall.assignedTo = this.previousAssignment;
        this.isReassignEditing = false;
    }

    async submit() {
        const result = await this._validator.validate();
        if (result.valid) {
            await this.assignToUser(this.assignedVesselPortCall.assignedTo);
        }
    }

    async unassign(event: any) {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        await this.assignToUser(null);
    }

    assignToMe = async (event: any) => {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        await this.assignToUser(this.userService.currentUser.id);
    }

    async assignToUser(userId: number) {
        if (userId == this.userService.currentUser.id) {
            this.assignmentComment = '';

            const canAssignToMe = !this.canAssignToMe || this.canAssignToMe();
            if (!canAssignToMe) return;
        }

        if (userId != this.previousAssignment) { // only save if different
            await this.save(userId);
        }
    }

    @computedFrom(Reflection.getPropertyPath<VesselPreArrivalAssignment>(z => z.assignedVesselPortCall),
    Reflection.getPropertyPath<VesselPreArrivalAssignment>(z => z.assignedVesselPortCall.assignedTo))
    get isUnassigned(): boolean {
        return !this.assignedVesselPortCall.assignedTo;
    }

    @computedFrom(Reflection.getPropertyPath<VesselPreArrivalAssignment>(z => z.isUnassigned),
    Reflection.getPropertyPath<VesselPreArrivalAssignment>(z => z.canSetUnassignedAfterAssignment))
    get canSetAsUnassigned(): boolean {
        if (this.isUnassigned) return true;

        if (!this.isUnassigned && !this.canSetUnassignedAfterAssignment) return false;

        return true;
    }

    @computedFrom(Reflection.getPropertyPath<VesselPreArrivalAssignment>(z => z.assignedVesselPortCall.assignedTo), 
    Reflection.getPropertyPath<VesselPreArrivalAssignment>(z => z.userService.currentUser),
    Reflection.getPropertyPath<VesselPreArrivalAssignment>(z => z.userService.currentUser.id))
    get isAssignedToMe(): boolean {
        return this.assignedVesselPortCall.assignedTo == this.userService.currentUser.id;
    }

    toggleAssignMenu($event) {
        $event.stopPropagation();
        this.isMenuOpen = !this.isMenuOpen;
    }

    toggleEditReassign($event){
        $event.stopPropagation();
        this.isReassignEditing = !this.isReassignEditing;
    }

    sortUsers = (users: LookupValue[]) => {
        return users.sort((a, b) => {
            const nameA = a.name.toUpperCase(); // ignore upper and lowercase
            const nameB = b.name.toUpperCase(); // ignore upper and lowercase
            if (nameA > nameB) return 1;
            if (nameA < nameB) return -1;
            return 0;
        });
    }
}